import { Device, DeviceStatusInfo, User } from "@fairfleet/geotab";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterItem,
  GridFilterModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { LoadFailedOverlay } from "@/components/grid/LoadFailedOverlay";
import { filterTileClick, KpiTile } from "@/components/layout/KpiTile";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { useDevicesBySearch } from "@/hooks/devices/useDevicesBySearch";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { formatDate } from "@/hooks/metrics/useFormattedDate";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useDebounced } from "@/hooks/useDebounced";
import { useUsersByUserIds } from "@/hooks/users/useUsersByUserIds";
import useFilters from "@/pages/diagnosticmonitor/hooks/useFilters";
import { getErrorMessage } from "@/utilities/error/getErrorMessage";
import { formatNumber } from "@/utilities/formatNumber";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";
import { isRealDriverId } from "@/utilities/user/isRealDriverId";

type AssetRow = {
  id: string;
  name: string;
  licensePlate: string;
  vin: string;
  serialNumber: string;
  comment: string;
  currentDriverId?: string;
  currentDriver: string;
  odometer: number | null;
  lastCommunication: Date | null;
  isOffline: boolean;
};

const OFFLINE_FILTER: GridFilterItem = { field: "isOffline", operator: "is", value: "true" };

const ASSET_OVERVIEW_FIELDS = [
  "name",
  "licenseplate",
  "serialNumber",
  "activeTo",
  "id",
  "vehicleIdentificationNumber",
  "groups",
  "customProperties",
  "comment",
];

const COLUMN_VISIBILITY: GridColumnVisibilityModel = { isOffline: false };

export function getStatusInfoDriverId(info?: DeviceStatusInfo): string | undefined {
  const id = info?.driver?.id;
  return isRealDriverId(id) ? id : undefined;
}

/** The real current driver ids across the status infos, each once. */
export function getCurrentDriverIds(statusInfoMap: Record<string, DeviceStatusInfo>): string[] {
  const ids = Object.values(statusInfoMap).map(getStatusInfoDriverId);
  return [...new Set(ids.filter((id): id is string => !!id))];
}

const ODOMETER_DIAGNOSTIC_IDS = ["DiagnosticOdometerId"];

/** Odometer in km from `DiagnosticOdometerId`, the value the diagnostic monitor shows as "Kilometerstand". */
export function getOdometerKm(info?: DeviceStatusInfo): number | null {
  if (!info) return null;
  const data = info.statusData?.find(sd => sd.diagnostic?.id === "DiagnosticOdometerId")?.data;
  return typeof data === "number" ? Math.round(data / 1000) : null;
}

/** One grid row per device, with the current driver ("Last, First") and odometer from the status info. */
export function buildAssetRows(
  devices: Device[],
  statusInfoMap: Record<string, DeviceStatusInfo>,
  driverById: Map<string, User>,
): AssetRow[] {
  return devices.map(device => {
    const rec = device as unknown as Record<string, unknown>;
    const info = statusInfoMap[device.id];
    const driverId = getStatusInfoDriverId(info);
    const driver = driverId ? driverById.get(driverId) : undefined;
    const lastComm = (info as { dateTime?: string | Date } | undefined)?.dateTime;
    // Same rule as the other pages; no status info at all does not count as offline.
    const isOffline = info ? !info.isDeviceCommunicating : false;

    return {
      id: device.id,
      name: device.name ?? "",
      licensePlate: getDeviceLicensePlate(device),
      vin: String(rec.vehicleIdentificationNumber ?? ""),
      serialNumber: device.serialNumber ?? "",
      comment: device.comment ?? "",
      currentDriverId: driverId,
      currentDriver: getUserDisplayName(driver, "lastFirst"),
      odometer: getOdometerKm(info),
      lastCommunication: lastComm ? new Date(lastComm) : null,
      isOffline,
    };
  });
}

/**
 * True while the odometer, last-communication and offline columns still wait for their status
 * lookup: on the first load, and while the previous search's map stands in for new vehicles.
 * Without vehicles the lookup is disabled and would keep the old placeholder forever.
 */
export function isStatusInfoPending(query: {
  deviceCount: number;
  isLoading: boolean;
  isPlaceholderData: boolean;
}): boolean {
  return query.deviceCount > 0 && (query.isLoading || query.isPlaceholderData);
}

type QueryState = { isLoading: boolean; isPlaceholderData: boolean; isError: boolean };

/**
 * Which sources the tiles and the export can trust. Placeholder data is the previous search's
 * result (keepPreviousData), so it counts as still loading. The status lookup is known once it is
 * no longer pending (isStatusInfoPending, which also covers the disabled lookup without vehicles)
 * and did not fail. The sheet carries the driver and odometer columns, so the export also waits
 * for those lookups.
 */
export function getAssetSourcesKnown(state: {
  devices: QueryState;
  statusInfo: QueryState;
  deviceCount: number;
  drivers: { isLoading: boolean; isError: boolean };
}): { devices: boolean; statusInfo: boolean; exportable: boolean } {
  const { devices: deviceQuery, statusInfo: statusQuery, deviceCount } = state;
  const devices = !deviceQuery.isLoading && !deviceQuery.isPlaceholderData && !deviceQuery.isError;
  const statusInfo =
    !isStatusInfoPending({
      deviceCount,
      isLoading: statusQuery.isLoading,
      isPlaceholderData: statusQuery.isPlaceholderData,
    }) &&
    (deviceCount === 0 || !statusQuery.isError);
  const drivers = !state.drivers.isLoading && !state.drivers.isError;

  return { devices, statusInfo, exportable: devices && statusInfo && drivers };
}

/**
 * The two tiles above the grid. A count is null (shown as "–") while its source is loading or
 * failed: an empty device list is not "0 assets" and a failed status lookup is not "0 offline".
 */
export function summarizeAssets(
  rows: AssetRow[],
  known: { devices: boolean; statusInfo: boolean },
): { total: number | null; offline: number | null } {
  if (!known.devices) return { total: null, offline: null };

  return {
    total: rows.length,
    offline: known.statusInfo ? rows.filter(row => row.isOffline).length : null,
  };
}

/**
 * A failed source makes the grid say "failed" instead of "no rows": the device list always, the
 * status lookup when an active filter on the offline column needs it.
 */
export function isAssetGridLoadFailed(state: {
  devicesError: boolean;
  statusInfoError: boolean;
  filterItems: readonly GridFilterItem[];
}): boolean {
  // A row without a value is inactive in MUI and filters nothing.
  const offlineFiltered = state.filterItems.some(
    item => item.field === OFFLINE_FILTER.field && item.value !== undefined && item.value !== "",
  );
  return state.devicesError || (state.statusInfoError && offlineFiltered);
}

export default function AssetsOverviewGrid({ onSelectDevice }: { onSelectDevice?: (device: Device) => void }) {
  const { i18n, t } = useTranslation();
  const [search, setSearch] = useState("");
  // The search runs on the server (a Device Get of up to 5000 objects), so wait for the typing to pause.
  const debouncedSearch = useDebounced(search, 300);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const { isFilterActive, toggleFilter } = useFilters(filterModel, setFilterModel);

  const {
    data: devices = [],
    isLoading,
    isPlaceholderData: isDevicesPlaceholder,
    isError: isDevicesError,
    error: devicesError,
  } = useDevicesBySearch(debouncedSearch, false, ASSET_OVERVIEW_FIELDS, {
    includeWithoutSerial: true,
    limit: RESULTS_LIMIT_DEVICE,
  });

  const deviceIds = useMemo(() => devices.map(d => d.id), [devices]);

  const statusInfoQuery = useStatusInfosByIds(deviceIds, ODOMETER_DIAGNOSTIC_IDS, 30_000);
  const { data: statusInfoMap = {}, isError: isStatusInfoError } = statusInfoQuery;
  // Named fields, not a spread: reading every field would re-render the grid on each isFetching flip.
  const statusInfoPending = isStatusInfoPending({
    deviceCount: deviceIds.length,
    isLoading: statusInfoQuery.isLoading,
    isPlaceholderData: statusInfoQuery.isPlaceholderData,
  });

  // Looked up by id, not by group: a vehicle in the selected groups may be driven by someone outside them.
  const currentDriverIds = useMemo(() => getCurrentDriverIds(statusInfoMap), [statusInfoMap]);
  const {
    data: driverUsers,
    isLoading: isDriversLoading,
    isError: isDriversError,
  } = useUsersByUserIds(currentDriverIds);
  const driverById = useMemo(() => {
    const map = new Map<string, User>();
    for (const user of driverUsers ?? []) if (user.id) map.set(user.id, user);
    return map;
  }, [driverUsers]);

  const rows = useMemo(() => buildAssetRows(devices, statusInfoMap, driverById), [devices, driverById, statusInfoMap]);

  const known = getAssetSourcesKnown({
    devices: { isLoading, isPlaceholderData: isDevicesPlaceholder, isError: isDevicesError },
    statusInfo: {
      isLoading: statusInfoQuery.isLoading,
      isPlaceholderData: statusInfoQuery.isPlaceholderData,
      isError: isStatusInfoError,
    },
    deviceCount: deviceIds.length,
    drivers: { isLoading: isDriversLoading, isError: isDriversError },
  });
  const summary = useMemo(
    () => summarizeAssets(rows, { devices: known.devices, statusInfo: known.statusInfo }),
    [rows, known.devices, known.statusInfo],
  );
  const exportDisabled = !known.exportable;

  const columns = useMemo<GridColDef<AssetRow>[]>(() => [
    {
      field: "name",
      headerName: t("assets.overview.columns.asset"),
      minWidth: 220,
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<AssetRow>) => (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: "100%", height: "100%" }}>
          <DirectionsCarIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {params.row.name}
          </Typography>
          {params.row.isOffline && (
            <Tooltip title={t("assets.overview.offline")}>
              <SignalWifiOffIcon fontSize="small" color="warning" sx={{ flexShrink: 0 }} />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: "currentDriver",
      headerName: t("assets.overview.columns.currentDriver"),
      minWidth: 180,
      flex: 1,
    },
    {
      field: "licensePlate",
      headerName: t("assets.overview.columns.licensePlate"),
      minWidth: 140,
      flex: 0.8,
    },
    {
      field: "vin",
      headerName: t("assets.fields.vin"),
      minWidth: 190,
      flex: 1,
    },
    {
      field: "odometer",
      headerName: t("assets.overview.columns.odometer"),
      minWidth: 160,
      flex: 0.8,
      type: "number",
      valueFormatter: (value: number | null) => formatNumber(value, i18n.language, { precision: 0, missing: "–" }),
    },
    {
      field: "lastCommunication",
      headerName: t("assets.lastCommunication"),
      minWidth: 190,
      flex: 1,
      type: "dateTime",
      valueFormatter: (value: Date | null) => (value ? formatDate(value, i18n.language) : "–"),
    },
    {
      field: "serialNumber",
      headerName: t("assets.overview.columns.serialNumber"),
      minWidth: 180,
      flex: 1,
    },
    {
      field: "comment",
      headerName: t("assets.overview.columns.comment"),
      minWidth: 200,
      flex: 1.5,
    },
    {
      field: "isOffline",
      headerName: t("assets.overview.offline"),
      type: "boolean",
      disableExport: true,
    },
  ], [i18n.language, t]);

  const showAllAssets = () => {
    setFilterModel(previous => ({
      ...previous,
      items: previous.items.filter(item => item.field !== OFFLINE_FILTER.field),
    }));
  };

  return (
    <Stack spacing={2} sx={{ flex: 1, height: "100%", minHeight: 0, minWidth: 0 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <KpiTile
            selected={!isFilterActive(OFFLINE_FILTER)}
            icon={<DirectionsCarIcon />}
            label={t("assets.overview.summary.total")}
            onClick={showAllAssets}
            value={formatNumber(summary.total, i18n.language, { precision: 0, missing: "–" })}
          />
          <KpiTile
            selected={isFilterActive(OFFLINE_FILTER)}
            icon={<SignalWifiOffIcon />}
            color="warning"
            label={t("assets.overview.summary.offline")}
            onClick={filterTileClick(summary.offline, isFilterActive(OFFLINE_FILTER), () =>
              toggleFilter(OFFLINE_FILTER),
            )}
            value={formatNumber(summary.offline, i18n.language, { precision: 0, missing: "–" })}
          />
        </Stack>

        <TextField
          size="small"
          label={t("assets.overview.search")}
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      {isDevicesError && (
        <Alert severity="error">
          {t("assets.overview.loadError")} {getErrorMessage(devicesError)}
        </Alert>
      )}

      {(isStatusInfoError || isDriversError) && (
        <Alert severity="warning">{t("assets.overview.enrichmentError")}</Alert>
      )}

      {devices.length >= RESULTS_LIMIT_DEVICE && (
        <Alert severity="warning">{t("assets.overview.truncated", { limit: RESULTS_LIMIT_DEVICE })}</Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 360 }}>
        <I18nGrid
          rows={rows}
          columns={columns}
          // Rows stay visible under a progress bar while they are the previous search's result or
          // while the status columns still wait for their lookup.
          loading={isLoading || isDevicesPlaceholder || statusInfoPending}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          slots={{
            toolbar: ExportToolbar,
            ...(isAssetGridLoadFailed({
              devicesError: isDevicesError,
              statusInfoError: isStatusInfoError,
              filterItems: filterModel.items,
            }) && { noRowsOverlay: LoadFailedOverlay }),
          }}
          slotProps={{
            loadingOverlay: { variant: "linear-progress", noRowsVariant: "skeleton" },
            toolbar: {
              report: { title: t("assets.title"), filter: debouncedSearch.trim() || undefined },
              fractionDigits: { odometer: 0 },
              exportDisabled,
              hideQuickFilter: true,
            },
          }}
          settingsKey="assets"
          sx={{ height: "100%" }}
          disableRowSelectionOnClick={!onSelectDevice}
          onRowClick={onSelectDevice ? params => {
            const device = devices.find(d => d.id === params.row.id);
            if (device) onSelectDevice(device);
          } : undefined}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            density: "compact",
            columns: { columnVisibilityModel: COLUMN_VISIBILITY },
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "name", sort: "asc" }] },
          }}
        />
      </Box>
    </Stack>
  );
}
